import React, { useState } from "react";
import { LogIn, Loader } from "lucide-react";
import { toast } from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

const OIDCLogin = () => {
  const { translate } = useLanguage();
  const { login, isLoading } = useAuth();
  const [isInitiating, setIsInitiating] = useState(false);
  const [error, setError] = useState(null);

  const handleLogin = async () => {
    try {
      setIsInitiating(true);
      setError(null);
      await login();
    } catch (error) {
      setError(error.message);
      toast.error(translate("loginFailed") || "Failed to initiate login");
    } finally {
      setIsInitiating(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--color-bg)" }}
      >
        <div className="text-center">
          <div
            className="w-10 h-10 rounded-full border-2 border-t-transparent mx-auto mb-4"
            style={{
              borderColor: "var(--color-mint)",
              borderTopColor: "transparent",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <p className="text-sm font-medium" style={{ color: "var(--color-text-secondary)" }}>
            {translate("loading") || "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: "var(--color-bg)" }}
    >
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="text-center mb-8">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: "var(--color-sidebar-bg)" }}
          >
            <img src="/favicon.svg" alt="Kodly" className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
            AI Agent Hub
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--color-mint)" }}>
            Backoffice
          </p>
        </div>

        {/* Login card */}
        <div className="card p-8">
          <div className="mb-6">
            <h2 className="text-lg font-semibold" style={{ color: "var(--color-text-primary)" }}>
              {translate("secureLogin") || "Sign In"}
            </h2>
            <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
              {translate("loginDescription") ||
                "Use your organization credentials to access the system"}
            </p>
          </div>

          <button
            onClick={handleLogin}
            disabled={isInitiating}
            className="btn-primary w-full justify-center py-2.5"
          >
            {isInitiating ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>{translate("redirecting") || "Redirecting..."}</span>
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" />
                <span>{translate("loginWithSSO") || "Login with SSO"}</span>
              </>
            )}
          </button>

          {error && (
            <div
              className="mt-4 p-3 rounded-lg text-sm"
              style={{
                backgroundColor: "var(--color-error-muted, rgba(239,68,68,0.08))",
                border: "1px solid var(--color-error-border, rgba(239,68,68,0.25))",
                color: "var(--color-error)",
              }}
            >
              {error}
            </div>
          )}

          <p
            className="mt-5 text-xs text-center"
            style={{ color: "var(--color-text-muted)" }}
          >
            {translate("loginHelp") ||
              "You will be redirected to your organization's login page"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OIDCLogin;
